import { useEffect } from "react";

/**
 * iter126 — SEO Head Helper
 * Manages <title>, meta tags, canonical URL, Open Graph, Twitter Card and
 * JSON-LD structured data by directly mutating <head>. Modern crawlers (Googlebot,
 * Bingbot, Yandex, DuckDuckBot) execute JS before indexing, so this is sufficient
 * for CSR SPAs.
 *
 * Usage:
 *   useSEO({ title: "Hakkımızda", description: "...", canonical: "/hakkimizda", jsonld: {...} });
 */
function upsertMeta(attrName, attrValue, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attrName, attrValue);
    tag.setAttribute("data-seo", "1");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}
function upsertLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    tag.setAttribute("data-seo", "1");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}
function upsertJsonLd(id, data) {
  if (!data) return;
  let tag = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!tag) {
    tag = document.createElement("script");
    tag.setAttribute("type", "application/ld+json");
    tag.setAttribute("data-jsonld", id);
    document.head.appendChild(tag);
  }
  try { tag.textContent = JSON.stringify(data); } catch {}
}

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

let _seoCache = null;
async function getSeo() {
  if (_seoCache) return _seoCache;
  try {
    const resp = await fetch(`${API}/api/seo/settings`);
    _seoCache = await resp.json();
    return _seoCache;
  } catch { return {}; }
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  image,
  ogType = "website",
  robots,
  jsonld,           // single or array
  breadcrumbs,      // [{name, path}]
} = {}) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const seo = await getSeo();
      if (cancelled) return;
      const finalTitle = title
        ? `${title} — ${seo.site_title?.split(" — ")[0] || "Bordo Seramik"}`
        : (seo.site_title || "Bordo Seramik");
      // iter163.3 — /panel altında "Bordo Yönetim" başlığını koru
      const isPanel = typeof window !== "undefined" && window.location.pathname.startsWith("/panel");
      if (!isPanel) {
        document.title = finalTitle;
      }
      const finalDesc = description || seo.site_description || "";
      const finalKeywords = keywords || seo.default_keywords || "";
      const finalRobots = robots || seo.meta_robots || "index, follow";
      const base = (seo.canonical_domain || window.location.origin).replace(/\/$/, "");
      const path = canonical || window.location.pathname;
      const finalCanonical = path.startsWith("http") ? path : `${base}${path}`;
      const finalImage = image || seo.default_og_image || "";

      upsertMeta("name", "description", finalDesc);
      upsertMeta("name", "keywords", finalKeywords);
      upsertMeta("name", "robots", finalRobots);
      upsertMeta("name", "author", "Bordo Seramik");
      upsertMeta("name", "language", "Turkish");
      upsertMeta("name", "geo.region", "TR-01");
      upsertMeta("name", "geo.placename", seo.primary_service_area || "Adana");
      upsertMeta("http-equiv", "content-language", "tr");
      upsertLink("canonical", finalCanonical);

      // Open Graph
      upsertMeta("property", "og:type", ogType);
      upsertMeta("property", "og:title", finalTitle);
      upsertMeta("property", "og:description", finalDesc);
      upsertMeta("property", "og:url", finalCanonical);
      upsertMeta("property", "og:site_name", seo.site_title?.split(" — ")[0] || "Bordo Seramik");
      upsertMeta("property", "og:locale", "tr_TR");
      if (finalImage) upsertMeta("property", "og:image", finalImage);

      // Twitter
      upsertMeta("name", "twitter:card", finalImage ? "summary_large_image" : "summary");
      upsertMeta("name", "twitter:title", finalTitle);
      upsertMeta("name", "twitter:description", finalDesc);
      if (finalImage) upsertMeta("name", "twitter:image", finalImage);

      // Verification codes
      if (seo.google_site_verification) upsertMeta("name", "google-site-verification", seo.google_site_verification);
      if (seo.bing_site_verification) upsertMeta("name", "msvalidate.01", seo.bing_site_verification);
      if (seo.yandex_verification) upsertMeta("name", "yandex-verification", seo.yandex_verification);

      // Breadcrumbs JSON-LD
      if (breadcrumbs && breadcrumbs.length > 0) {
        upsertJsonLd("breadcrumb", {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem", position: i + 1, name: b.name,
            item: b.path.startsWith("http") ? b.path : `${base}${b.path}`,
          })),
        });
      }

      // Page JSON-LD
      if (jsonld) {
        const items = Array.isArray(jsonld) ? jsonld : [jsonld];
        items.forEach((data, i) => upsertJsonLd(`page-${i}`, data));
      }

      // iter153.15 — Site-genelinde Organization + WebSite + LocalBusiness JSON-LD
      // Bu 3 schema Google'ın site'ı tanıması + Sitelink Searchbox + Google My Business bağlantısı için kritik
      const siteName = seo.site_title?.split(" — ")[0] || "Bordo Seramik";

      upsertJsonLd("global-organization", {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteName,
        url: base,
        logo: seo.default_og_image || `${base}/logo.png`,
        description: seo.site_description || "",
        foundingDate: seo.founding_year ? `${seo.founding_year}` : undefined,
        address: {
          "@type": "PostalAddress",
          addressLocality: seo.primary_service_area || "Adana",
          addressRegion: "Adana",
          addressCountry: "TR",
        },
        areaServed: [
          { "@type": "City", name: seo.primary_service_area || "Adana" },
          { "@type": "AdministrativeArea", name: "Çukurova" },
          { "@type": "AdministrativeArea", name: "Sarıçam" },
          { "@type": "AdministrativeArea", name: "Seyhan" },
        ],
        sameAs: [
          seo.facebook_url, seo.instagram_url, seo.linkedin_url, seo.youtube_url,
          seo.twitter_url, seo.tiktok_url,
        ].filter(Boolean),
      });

      upsertJsonLd("global-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteName,
        url: base,
        potentialAction: {
          "@type": "SearchAction",
          target: `${base}/urunler?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
        inLanguage: "tr-TR",
      });

      // LocalBusiness — Adana bölgesindeki mağaza konumları
      if (seo.gmb_place_id || seo.primary_service_area) {
        upsertJsonLd("global-localbusiness", {
          "@context": "https://schema.org",
          "@type": ["Store", "HomeAndConstructionBusiness"],
          "@id": `${base}/#business`,
          name: siteName,
          image: seo.default_og_image || `${base}/logo.png`,
          url: base,
          telephone: seo.phone || "+90 322 000 00 00",
          priceRange: seo.price_range || "₺₺",
          currenciesAccepted: "TRY",
          paymentAccepted: "Nakit, Kredi Kartı, Havale, Çek, Senet",
          address: {
            "@type": "PostalAddress",
            addressLocality: seo.primary_service_area || "Adana",
            addressRegion: "Adana",
            addressCountry: "TR",
          },
          areaServed: ["Adana", "Çukurova", "Sarıçam", "Seyhan", "Mersin", "Osmaniye"],
          openingHours: seo.opening_hours_default || "Mo-Sa 09:00-19:00",
          hasMap: seo.gmb_place_id ? `https://www.google.com/maps/place/?q=place_id:${seo.gmb_place_id}` : undefined,
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, JSON.stringify(jsonld), JSON.stringify(breadcrumbs)]);
}

export default useSEO;
